import type { PenAnchorPoint } from '@/lib/elements/types/pen-anchor-point'
import { PenSubpathRange } from '@/lib/elements/types/pen-subpath-range'

/**
 * Parse an SVG path string (M, L, C, Q, Z commands) back into PenAnchorPoint[], split into
 * per-contour `subpaths` ranges. Coordinates are in the path's own coordinate space (local to the
 * shape bounding box). `isClosed` is kept for backward compatibility with single-subpath callers
 * — it mirrors the first (usually only) subpath's own closed state.
 */
export function parseSvgPathToAnchors(d: string): {
  points: PenAnchorPoint[]
  isClosed: boolean
  subpaths: PenSubpathRange[]
} {
  const tokens = d.trim().match(/[MmLlCcQqZz]|[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/g) ?? []
  const points: PenAnchorPoint[] = []
  const subpaths: PenSubpathRange[] = []
  let subpathStart = 0
  let i = 0

  function nextNum(): number {
    return parseFloat(tokens[++i] ?? '0')
  }

  // Finalize the current subpath [subpathStart, points.length). When `isClosedNow` came from an
  // explicit Z, merge a last-point-duplicates-first-point closing artifact into the subpath's own
  // first point — scoped to this subpath only, never the whole path's global first/last point.
  function finalizeSubpath(isClosedNow: boolean): void {
    if (points.length <= subpathStart) return

    if (isClosedNow && points.length - subpathStart >= 2) {
      const last = points[points.length - 1]
      const first = points[subpathStart]
      const EPSILON = 0.5

      if (Math.abs(last.x - first.x) < EPSILON && Math.abs(last.y - first.y) < EPSILON) {
        if (last.handleIn) {
          first.handleIn = last.handleIn
        }

        points.pop()
      }
    }

    subpaths.push({ start: subpathStart, end: points.length, isClosed: isClosedNow })
    subpathStart = points.length
  }

  while (i < tokens.length) {
    const cmd = tokens[i]

    switch (cmd) {
      case 'M':
      case 'm': {
        // A new M starts a new subpath. If the previous one was never closed by an explicit Z,
        // it was left open (e.g. mid pen-tool drawing) — finalize it as such before starting.
        finalizeSubpath(false)

        const x = nextNum()
        const y = nextNum()

        points.push({ id: uid(), x, y, type: 'corner', handleIn: null, handleOut: null })
        i++
        break
      }

      case 'L':
      case 'l': {
        const x = nextNum()
        const y = nextNum()

        points.push({ id: uid(), x, y, type: 'corner', handleIn: null, handleOut: null })
        i++
        break
      }

      case 'C':
      case 'c': {
        const cx1 = nextNum()
        const cy1 = nextNum()
        const cx2 = nextNum()
        const cy2 = nextNum()
        const ex = nextNum()
        const ey = nextNum()

        if (points.length > 0) {
          const prev = points[points.length - 1]

          // Only store a real handle when the control point differs from the anchor;
          // a coincident control point is a degenerate placeholder, not a true handle.
          if (Math.abs(cx1 - prev.x) > 0.5 || Math.abs(cy1 - prev.y) > 0.5) {
            prev.handleOut = { x: cx1, y: cy1 }
          }
        }

        const handleIn =
          Math.abs(cx2 - ex) > 0.5 || Math.abs(cy2 - ey) > 0.5 ? { x: cx2, y: cy2 } : null

        points.push({ id: uid(), x: ex, y: ey, type: 'corner', handleIn, handleOut: null })
        i++
        break
      }

      case 'Q':
      case 'q': {
        // Quadratic bezier (TrueType glyph outlines from opentype.js emit these — this codebase's
        // own path generation never has, which is why this case didn't exist until text-to-shape
        // needed to round-trip real font curves). PenAnchorPoint only models cubic handles, so
        // elevate the single quadratic control point QC to the two cubic control points that trace
        // an IDENTICAL curve: cp1 = P0 + 2/3*(QC-P0), cp2 = P1 + 2/3*(QC-P1).
        const qx = nextNum()
        const qy = nextNum()
        const ex = nextNum()
        const ey = nextNum()

        if (points.length > 0) {
          const prev = points[points.length - 1]
          const cx1 = prev.x + (2 / 3) * (qx - prev.x)
          const cy1 = prev.y + (2 / 3) * (qy - prev.y)

          if (Math.abs(cx1 - prev.x) > 0.5 || Math.abs(cy1 - prev.y) > 0.5) {
            prev.handleOut = { x: cx1, y: cy1 }
          }
        }

        const cx2 = ex + (2 / 3) * (qx - ex)
        const cy2 = ey + (2 / 3) * (qy - ey)
        const handleIn =
          Math.abs(cx2 - ex) > 0.5 || Math.abs(cy2 - ey) > 0.5 ? { x: cx2, y: cy2 } : null

        points.push({ id: uid(), x: ex, y: ey, type: 'corner', handleIn, handleOut: null })
        i++
        break
      }

      case 'Z':
      case 'z': {
        // We only treat a subpath as closed when Z was actually present — an open subpath whose
        // last point happens to land near its first is NOT treated as closed (that would break
        // endpoint continuation in pen-edit mode, where isClosed=true suppresses endpoint hits).
        finalizeSubpath(true)
        i++
        break
      }

      default:
        i++
    }
  }

  // Trailing subpath with no closing Z (e.g. an in-progress or deliberately open pen path).
  finalizeSubpath(false)

  // Infer smooth type: both handles exist and handleOut mirrors handleIn through the anchor.
  // Purely per-point — unaffected by subpath boundaries.
  const SMOOTH_EPSILON = 0.5

  for (const pt of points) {
    if (pt.handleIn && pt.handleOut) {
      const mirrorX = pt.x * 2 - pt.handleIn.x
      const mirrorY = pt.y * 2 - pt.handleIn.y

      if (
        Math.abs(mirrorX - pt.handleOut.x) < SMOOTH_EPSILON &&
        Math.abs(mirrorY - pt.handleOut.y) < SMOOTH_EPSILON
      ) {
        pt.type = 'smooth'
      }
    }
  }

  return { points, isClosed: subpaths[0]?.isClosed ?? false, subpaths }
}

export function uid(): string {
  return Math.random().toString(36).slice(2, 10)
}
